import { getCoreUtils, kCore, SetCoreUtils, setProductVersion } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppErrorReporter, IAppErrorEvent, IAppErrorPage, IAppErrorShow, IAppErrorSink } from "./AppErrorReport";
import { WAC_VERSION } from "./AppShared";

// AppErrorInstall - puts the app's error handler (`AppErrorReporter`) behind `getCoreUtils()` BEFORE ANY OTHER CODE
// OF THE APP RUNS (2026-09-28). The browser half of `AppErrorReport`: it touches `window`, `document` and `navigator`,
// so the Node suites import the rules file, never this one.
//
// ⛔ THE ORDER IS THE WHOLE POINT. `AppMain.ts` imports this module FIRST, and ES modules evaluate in import order, so
// the module-scope `AppErrorReporting.Install(...)` at the bottom runs before every other module of the bundle -
// including the static singletons whose construction could already report. `AppMain.Start` calls `Install` again as
// its first statement (idempotent), so a harness that builds `WACApp` by hand is covered too. Pinned by
// `AppErrorReport.test.ts` against the source of `AppMain.ts`.
// ⭐ It also catches what nothing caught: window `error` and `unhandledrejection` go through the same handler.
// ⚠ `kCore.KApplication.name/version` and `setProductVersion` are set here - `kCore.KReportError` stamps the version
// from `KApplication`, and an empty one sends a report no build can be traced from.

/** The live page's address and user agent. DOCUMENTED EXCEPTION: read inside the error handler - no guard, and
 *  ERROR-SUPPRESSED-JUSTIFIED: a page that cannot say where it is reports "" (nothing leaks, the report still goes). */
export class AppErrorWindowPage implements IAppErrorPage {
    public Href = (): string => {
        try {
            return String(window.location.href);
        } catch (e) {
            return "";
        }
    };

    public Agent = (): string => {
        try {
            return String(window.navigator.userAgent);
        } catch (e) {
            return "";
        }
    };
}

/** The shared reporter: `kCore.KReportError` (the POST to `kCore.KServicesURL`, de-duplicated, version-stamped). */
export class AppErrorCoreSink implements IAppErrorSink {
    public Send = (Data: Record<string, unknown>): void => {
        // DOCUMENTED EXCEPTION: the reporting path itself - `kCore.KReportError` never throws (its own catch), and a
        // guard here would silence the report at the only moment it matters.
        kCore.KReportError(Data);
    };
}

/**
 * The handler's one line above the app: `p#wac-fault.wac-fault[role=alert]` (styled by `app.css`, no inline style -
 * the Content-Security-Policy has none). DOCUMENTED EXCEPTION: runs inside the handler with the gate latched, so it
 * carries no guard; ERROR-SUPPRESSED-JUSTIFIED: a page with no `#wac-app` simply shows nothing more.
 */
export class AppErrorBanner implements IAppErrorShow {
    public static readonly Id: string = "wac-fault";

    public Show = (Text: string): void => {
        try {
            if (document.getElementById(AppErrorBanner.Id) !== null) { return; }
            const Where: HTMLElement | null = document.getElementById("wac-app");
            if (Where === null || Where.parentNode === null) { return; }
            const Line: HTMLElement = document.createElement("p");
            Line.id = AppErrorBanner.Id;
            Line.className = "wac-fault";
            Line.setAttribute("role", "alert");
            Line.textContent = Text;
            Where.parentNode.insertBefore(Line, Where);
        } catch (e) {
            return;
        }
    };
}

/** What `Install` needs of a window (the real one, or a test's fake). */
export interface IAppErrorWindow {
    addEventListener(Type: string, Listener: (Ev: IAppErrorEvent) => void): void;
}

export class AppErrorReporting {
    /** The installed handler, or null before `Install`. */
    public static Current: AppErrorReporter | null = null;

    /**
     * Installs `Made` as THE handler (once per page: a second call keeps the first). Answers the handler in place.
     * DOCUMENTED EXCEPTION: this installs the handler the guard would read, so there is nothing to guard with yet;
     * ERROR-SUPPRESSED-JUSTIFIED: an install that fails leaves the core's console handler in place and says so on the
     * console - there is no reporter to report to.
     */
    public static Install = (Win: IAppErrorWindow | null, Made: AppErrorReporter): AppErrorReporter => {
        try {
            if (AppErrorReporting.Current !== null && getCoreUtils() === AppErrorReporting.Current) { return AppErrorReporting.Current; }
            kCore.KApplication.name = "WACommander";
            kCore.KApplication.version = WAC_VERSION;
            setProductVersion(WAC_VERSION);
            SetCoreUtils(Made);
            AppErrorReporting.Current = Made;
            if (Win !== null) {
                Win.addEventListener("error", Made.OnWindowError);
                Win.addEventListener("unhandledrejection", Made.OnRejection);
            }
            return Made;
        } catch (e) {
            console.error("[WACommander] the error handler could not be installed: ", e);
            return Made;
        }
    };

    /** The browser's handler: this window, `kCore.KReportError`, the banner. */
    public static InstallInBrowser = (): AppErrorReporter => {
        return AppErrorReporting.Install(window as unknown as IAppErrorWindow,
            new AppErrorReporter(new AppErrorWindowPage(), new AppErrorCoreSink(), new AppErrorBanner(), WAC_VERSION));
    };
}

// ⛔ FIRST THING THE BUNDLE DOES (see the header). `typeof window` keeps a Node process that imports this file by
// mistake from reaching `window` - the suites import `AppErrorReport`, never this.
if (typeof window !== "undefined" && typeof document !== "undefined") {
    AppErrorReporting.InstallInBrowser();
}
